import { sym } from "rdflib";
import { AddressBookQuery, ContactQuery, GroupQuery } from "./queries/index.js";
import { createAddressBook, createNewContact, createNewGroup, renameContact, updatePhoneNumber, updateEmailAddress, addContactToGroup, removePhoneNumber, addNewPhoneNumber, removeEmailAddress, addNewEmailAddress, removeContactFromGroup, } from "./update-operations/index.js";
import { executeUpdate, ModuleSupport, PreferencesQuery, ProfileQuery, TypeIndexQuery, addInstanceToTypeIndex, } from "@solid-data-modules/rdflib-utils";
import { vcard } from "./namespaces.js";
const VCARD_ADDRESS_BOOK = vcard("AddressBook");
export class ContactsModuleRdfLib {
    constructor(config) {
        this.support = new ModuleSupport(config);
        this.store = config.store;
        this.fetcher = config.fetcher;
        this.updater = config.updater;
    }
    async readAddressBook(uri) {
        const addressBookNode = sym(uri);
        await this.support.fetchNode(addressBookNode);
        const query = new AddressBookQuery(this.store, addressBookNode);
        const title = query.queryTitle();
        const nameEmailIndex = query.queryNameEmailIndex();
        const groupIndex = query.queryGroupIndex();
        await this.support.fetchAll([nameEmailIndex, groupIndex]);
        const contacts = query.queryContacts();
        const groups = query.queryGroups();
        return {
            uri,
            title,
            contacts,
            groups,
        };
    }
    async createAddressBook({ containerUri, name, ownerWebId, }) {
        const operation = createAddressBook(containerUri, name);
        await executeUpdate(this.fetcher, this.updater, operation);
        if (ownerWebId) {
            await this.updatePrivateTypeIndex(ownerWebId, operation.uri);
        }
        return operation.uri;
    }
    async updatePrivateTypeIndex(ownerWebId, addressBookUri) {
        const profileNode = sym(ownerWebId);
        await this.support.fetchNode(profileNode);
        const profileQuery = new ProfileQuery(profileNode, this.store);
        const preferencesFile = profileQuery.queryPreferencesFile();
        const privateTypeIndex = await this.fetchPrivateTypeIndex(profileNode, preferencesFile);
        if (!privateTypeIndex) {
            throw new Error(`Private type not found for WebID ${ownerWebId}.`);
        }
        const operation = addInstanceToTypeIndex(privateTypeIndex, addressBookUri, VCARD_ADDRESS_BOOK);
        await executeUpdate(this.fetcher, this.updater, operation);
    }
    async createNewContact({ addressBookUri, contact, groupUris, }) {
        const contactQuery = await this.executeCreateNewContact(addressBookUri, contact);
        await this.executeAddContactToGroups(groupUris, contactQuery);
        return contactQuery.contactNode.uri;
    }
    async executeAddContactToGroups(groupUris, contactQuery) {
        const groupNodes = (groupUris ?? []).map((it) => sym(it));
        await this.support.fetchAll(groupNodes);
        const groupUpdates = groupNodes.map((groupNode) => {
            const groupQuery = new GroupQuery(this.store, groupNode);
            const operation = addContactToGroup(contactQuery, groupQuery);
            return executeUpdate(this.fetcher, this.updater, operation);
        });
        await Promise.allSettled(groupUpdates);
    }
    async executeCreateNewContact(addressBookUri, contact) {
        const addressBookNode = sym(addressBookUri);
        await this.support.fetchNode(addressBookNode);
        const operation = createNewContact(new AddressBookQuery(this.store, addressBookNode), contact);
        await executeUpdate(this.fetcher, this.updater, operation);
        return new ContactQuery(this.store, sym(operation.uri));
    }
    async readContact(uri) {
        const contactNode = sym(uri);
        await this.support.fetchNode(contactNode);
        const query = new ContactQuery(this.store, contactNode);
        const name = query.queryName();
        const emails = query.queryEmails();
        const phoneNumbers = query.queryPhoneNumbers();
        return {
            uri,
            name,
            emails,
            phoneNumbers,
        };
    }
    async createNewGroup({ addressBookUri, groupName }) {
        const addressBookNode = sym(addressBookUri);
        await this.support.fetchNode(addressBookNode);
        const query = new AddressBookQuery(this.store, addressBookNode);
        const operation = createNewGroup(query, groupName);
        await executeUpdate(this.fetcher, this.updater, operation);
        return operation.uri;
    }
    async readGroup(uri) {
        const groupNode = sym(uri);
        await this.support.fetchNode(groupNode);
        const query = new GroupQuery(this.store, groupNode);
        const name = query.queryName();
        const members = query.queryMembers();
        return {
            uri,
            name,
            members,
        };
    }
    async addContactToGroup({ contactUri, groupUri }) {
        const contactNode = sym(contactUri);
        const groupNode = sym(groupUri);
        await this.support.fetchNode(contactNode);
        const contactQuery = new ContactQuery(this.store, contactNode);
        const groupQuery = new GroupQuery(this.store, groupNode);
        const operation = addContactToGroup(contactQuery, groupQuery);
        await executeUpdate(this.fetcher, this.updater, operation);
    }
    async removeContactFromGroup({ contactUri, groupUri, }) {
        const contactNode = sym(contactUri);
        const groupNode = sym(groupUri);
        await this.support.fetchNode(groupNode);
        const contactQuery = new ContactQuery(this.store, contactNode);
        const groupQuery = new GroupQuery(this.store, groupNode);
        const operation = removeContactFromGroup(contactQuery, groupQuery);
        await executeUpdate(this.fetcher, this.updater, operation);
    }
    async addNewPhoneNumber({ contactUri, newPhoneNumber, }) {
        const contactNode = sym(contactUri);
        const operation = addNewPhoneNumber(contactNode, newPhoneNumber);
        await executeUpdate(this.fetcher, this.updater, operation);
        return operation.uri;
    }
    async addNewEmailAddress({ contactUri, newEmailAddress, }) {
        const contactNode = sym(contactUri);
        const operation = addNewEmailAddress(contactNode, newEmailAddress);
        await executeUpdate(this.fetcher, this.updater, operation);
        return operation.uri;
    }
    async removePhoneNumber({ contactUri, phoneNumberUri, }) {
        const contactNode = sym(contactUri);
        const phoneNumberNode = sym(phoneNumberUri);
        await this.support.fetchNode(phoneNumberNode);
        const operation = removePhoneNumber(contactNode, phoneNumberNode, this.store);
        await executeUpdate(this.fetcher, this.updater, operation);
    }
    async removeEmailAddress({ contactUri, emailAddressUri, }) {
        const contactNode = sym(contactUri);
        const emailAddressNode = sym(emailAddressUri);
        await this.support.fetchNode(emailAddressNode);
        const operation = removeEmailAddress(contactNode, emailAddressNode, this.store);
        await executeUpdate(this.fetcher, this.updater, operation);
    }
    async updatePhoneNumber({ phoneNumberUri, newPhoneNumber, }) {
        const phoneNumberNode = sym(phoneNumberUri);
        await this.support.fetchNode(phoneNumberNode);
        const operation = updatePhoneNumber(phoneNumberNode, newPhoneNumber, this.store);
        await executeUpdate(this.fetcher, this.updater, operation);
    }
    async updateEmailAddress({ emailAddressUri, newEmailAddress, }) {
        const emailAddressNode = sym(emailAddressUri);
        await this.support.fetchNode(emailAddressNode);
        const operation = updateEmailAddress(emailAddressNode, newEmailAddress, this.store);
        await executeUpdate(this.fetcher, this.updater, operation);
    }
    async listAddressBooks(webId) {
        const profileNode = sym(webId);
        await this.support.fetchNode(profileNode);
        const profileQuery = new ProfileQuery(profileNode, this.store);
        const publicTypeIndexNode = profileQuery.queryPublicTypeIndex();
        const preferencesFile = profileQuery.queryPreferencesFile();
        const promisePublicUris = this.fetchIndexedAddressBooks(publicTypeIndexNode);
        const promisePrivateTypeIndex = this.fetchPrivateTypeIndex(profileNode, preferencesFile);
        const [publicUris, privateTypeIndex] = await Promise.allSettled([
            promisePublicUris,
            promisePrivateTypeIndex,
        ]);
        const privateUris = privateTypeIndex.status === "fulfilled"
            ? await this.fetchIndexedAddressBooks(privateTypeIndex.value)
            : [];
        return {
            publicUris: publicUris.status === "fulfilled" ? publicUris.value : [],
            privateUris,
        };
    }
    async fetchIndexedAddressBooks(publicTypeIndexNode) {
        if (!publicTypeIndexNode) {
            return [];
        }
        await this.support.fetchNode(publicTypeIndexNode);
        return new TypeIndexQuery(this.store, publicTypeIndexNode).queryInstancesForClass(VCARD_ADDRESS_BOOK);
    }
    async fetchPrivateTypeIndex(profileNode, preferencesFile) {
        if (!preferencesFile) {
            return null;
        }
        await this.support.fetchNode(preferencesFile);
        const preferencesQuery = new PreferencesQuery(this.store, profileNode, preferencesFile);
        return preferencesQuery.queryPrivateTypeIndex();
    }
    async renameContact({ contactUri, newName }) {
        const contactNode = sym(contactUri);
        await this.support.fetchNode(contactNode);
        const operation = renameContact(this.store, contactNode, newName);
        await executeUpdate(this.fetcher, this.updater, operation);
    }
}
//# sourceMappingURL=ContactsModuleRdfLib.js.map